import { skipToken } from '@reduxjs/toolkit/query/react';
import { useCallback } from 'react';
import { useGetPendingOfferCutscenesQuery, useMarkOfferCutsceneSeenMutation } from '@sorrem-client/ui/store';
import { useCurrentCharacterArgs } from './use-current-character-id';

export interface OfferCutsceneState {
    /**
     * The offer cutscene the current character still has to see before
     * accepting a commission, or null when there is none, while loading, or if
     * the request failed. A failure deliberately reads as "nothing pending": a
     * cutscene must never keep a character from their commission.
     */
    cutsceneId: string | null;
    isLoading: boolean;
    /** Records the cutscene as watched or skipped. Failures are swallowed; it just shows again next time. */
    markSeen: (cutsceneId: string) => Promise<void>;
}

/**
 * The meeting cutscene that plays before a commission can be accepted (a
 * commission's `offerCutscene`; see docs/gameplay/game-intro.md). The server
 * decides which are still unseen.
 */
export function useOfferCutscene(): OfferCutsceneState {
    const args = useCurrentCharacterArgs();
    const [markOfferCutsceneSeen] = useMarkOfferCutsceneSeenMutation();
    const { data, isLoading } = useGetPendingOfferCutscenesQuery(args, { refetchOnMountOrArgChange: true });

    const markSeen = useCallback(async (cutsceneId: string): Promise<void> => {
        if (args === skipToken) {
            return;
        }
        try {
            await markOfferCutsceneSeen({ ...args, cutsceneId }).unwrap();
        } catch {
            // Not recording it only means the cutscene plays again next time.
        }
    }, [args, markOfferCutsceneSeen]);

    return { cutsceneId: data?.[0]?.cutsceneId ?? null, isLoading: args !== skipToken && isLoading, markSeen };
}
